// @src/pre-deployment/src/data/useDataService.js v1.3
// - React hooks for DataService integration
// - v1.3: Add useChapterTracking to default export object so ChapterPage/build can use useDataService.useChapterTracking
// - Provides easy-to-use hooks for all components
// - Handles loading states, errors, and caching
// - Includes performance optimization and error boundaries
// - ENHANCED: Better error handling, retry logic, offline support, request deduplication
// - ENHANCED v1.2: Throws ValidationError for ErrorBoundary (not silent error returns)

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { writeRawError } from '../components/ErrorHandling/AutomatedErrorSystem/RawErrorService';
import {
  useChapterTracking,
  useLearningProgress,
  useMasteryData,
  useContentDiscovery,
  useKnowledgeProfile
} from '../tracking/hooks.js';
import { TrackingV2Service } from '../tracking/trackingService.js';
import { ValidationError } from '../utils/validationSchemas';

import {
  GrammarService,
  VocabularyService,
  DialogueService,
  ExerciseService,
  ProgressService,
  ContentService,
  DataUtils,
  getLearningModulesConfig,
  TrackingDataServiceV2,
  DataParametersServiceV2,
  DataService
} from './DataService';

// Fallback modules order for safe loading - ONLY used when Firestore is completely unavailable
const fallbackModulesOrder = [
  'chapterTitle', 'dialogue', 'vocabulary', 'translation', 'grammar', 'cloze', 'shapePuzzle', 'complete'
];

// Enhanced base hook for data fetching with better error handling
// ENHANCED v1.2: Throws ValidationError for ErrorBoundary to catch
const useAsyncData = (asyncFunction, dependencies = [], options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [shouldThrow, setShouldThrow] = useState(false);

  const {
    enabled = true,
    refetchOnMount = true,
    retryOnError = true,
    maxRetries = 3,
    retryDelay = 1000,
    timeout = 30000, // 30 second timeout to prevent infinite loading
    throwOnError = true // ENHANCED v1.2: Default to throwing for ErrorBoundary
  } = options;

  // Use refs for values that shouldn't trigger re-creation of fetchData
  const mountedRef = useRef(true);
  const asyncFunctionRef = useRef(asyncFunction);
  asyncFunctionRef.current = asyncFunction;
  const timeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  // Create a stable key for dependencies to avoid identity issues with array literals
  const depsKey = JSON.stringify(dependencies);

  const fetchData = useCallback(async (isRefresh = false, isRetry = false) => {
    if (!mountedRef.current) return;

    // Clear any existing timeouts/retries
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);

    if (isRefresh) {
      setRefreshing(true);
    } else if (!isRetry) {
      setLoading(true);
    }

    setError(null);
    setShouldThrow(false);

    // Set timeout
    timeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        const timeoutError = new ValidationError(
          'Data fetching timed out',
          [{ path: 'timeout', expected: `response within ${timeout}ms`, got: 'timeout', message: 'Request exceeded timeout limit' }],
          { timeout, dependencies }
        );
        setError(timeoutError);
        setLoading(false);
        setRefreshing(false);
        if (throwOnError) {
          setShouldThrow(true);
        }
      }
    }, timeout);

    try {
      const result = await asyncFunctionRef.current();

      if (mountedRef.current) {
        setData(result);
        setError(null);
        setRetryCount(0);
        setShouldThrow(false);
      }
    } catch (err) {
      console.error('useAsyncData error:', err);

      await writeRawError(err, {
        componentName: `useAsyncData_${asyncFunction.name || 'anonymous'}`,
        context: 'useAsyncData',
        dependencies: dependencies,
        retryCount: retryCount,
        functionName: asyncFunction.name || 'anonymous',
        isValidationError: err instanceof ValidationError || err.isValidationError,
        validationErrors: err.errors || [],
        severity: 'high'
      });

      if (mountedRef.current) {
        setError(err);

        // Handle retry logic - but NOT for ValidationErrors (data is invalid, retrying won't help)
        const isValidationError = err instanceof ValidationError || err.isValidationError;
        if (retryOnError && retryCount < maxRetries && !isValidationError) {
          retryTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              setRetryCount(prev => prev + 1);
              fetchData(false, true);
            }
          }, retryDelay);
        } else if (throwOnError) {
          // ENHANCED v1.2: Set flag to throw on next render for ErrorBoundary
          setShouldThrow(true);
        }
      }
    } finally {
      if (mountedRef.current) {
        clearTimeout(timeoutRef.current);
        setLoading(false);
        setRefreshing(false);
      }
    }
    // We use depsKey as a stable representation of the dependencies array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryOnError, maxRetries, retryDelay, timeout, depsKey, retryCount, throwOnError]);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled && refetchOnMount) {
      fetchData();
    }

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [enabled, refetchOnMount, fetchData]);

  // ENHANCED v1.2: Throw error for ErrorBoundary to catch
  // This is done during render so ErrorBoundary can catch it
  if (shouldThrow && error && !loading) {
    throw error;
  }

  return {
    data,
    loading,
    error,
    refreshing,
    refresh: () => fetchData(true),
    retry: () => {
      setRetryCount(0);
      setShouldThrow(false);
      fetchData();
    }
  };
};

// Grammar Data Hooks
export const useGrammarPoints = (level, chapter, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => GrammarService.getGrammarPoints(level, chapter, institution),
    [level, chapter, institution],
    options
  );
};

export const useGrammarPoint = (level, chapter, grammarId, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => GrammarService.getGrammarPoint(level, chapter, grammarId, institution),
    [level, chapter, grammarId, institution],
    options
  );
};

export const useGrammarChapters = (level, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => GrammarService.getAvailableChapters(level, institution),
    [level, institution],
    options
  );
};

export const useComprehensiveGrammar = (level, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => GrammarService.getComprehensiveGrammar(level, institution),
    [level, institution],
    options
  );
};

export const useGrammarExercises = (level, chapter, grammarId, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => GrammarService.getGrammarExercises(level, chapter, grammarId, institution),
    [level, chapter, grammarId, institution],
    options
  );
};

// Vocabulary Data Hooks
export const useVocabulary = (level, chapter, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => VocabularyService.getVocabulary(level, chapter, institution),
    [level, chapter, institution],
    options
  );
};

export const useVocabularyWord = (level, chapter, wordId, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => VocabularyService.getVocabularyWord(level, chapter, wordId, institution),
    [level, chapter, wordId, institution],
    options
  );
};

export const useVocabularyChapters = (level, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => VocabularyService.getAvailableChapters(level, institution),
    [level, institution],
    options
  );
};

export const useVocabularySearch = (searchTerm, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => VocabularyService.searchVocabulary(searchTerm, institution),
    [searchTerm, institution],
    { ...options, enabled: !!searchTerm && searchTerm.length >= 2 }
  );
};

// Dialogue Data Hooks
export const useDialogueData = (level, chapter, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => DialogueService.getDialogueScenes(level, chapter, institution),
    [level, chapter, institution],
    options
  );
};

export const useDialogueScene = (level, chapter, sceneId, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => DialogueService.getDialogueScene(level, chapter, sceneId, institution),
    [level, chapter, sceneId, institution],
    { ...options, enabled: !!sceneId }
  );
};

// Exercise Data Hooks
export const useClozeExercises = (level, chapter, institution = 'Korea_University', userId = null, options = {}) => {
  return useAsyncData(
    () => ExerciseService.generateClozeExercises(level, chapter, institution, userId),
    [level, chapter, institution, userId],
    {
      ...options,
      enabled: level && chapter
    }
  );
};

export const useShapePuzzleExercises = (level, chapter, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => ExerciseService.generateShapePuzzleExercises(level, chapter, institution),
    [level, chapter, institution],
    options
  );
};

export const useTranslationExercises = (level, chapter, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => ExerciseService.generateTranslationExercises(level, chapter, institution),
    [level, chapter, institution],
    options
  );
};

// Progress Tracking Hooks
export const useUserProgress = (userId, options = {}) => {
  return useAsyncData(
    () => ProgressService.getUserProgress(userId),
    [userId],
    { ...options, enabled: !!userId }
  );
};

export const useLessonMastery = (userId, lessonId, options = {}) => {
  return useAsyncData(
    () => ProgressService.getLessonMastery(userId, lessonId),
    [userId, lessonId],
    { ...options, enabled: !!userId && !!lessonId }
  );
};

// Content Discovery Hooks
export const useAvailableContent = (institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => ContentService.getAvailableContent(institution),
    [institution],
    options
  );
};

export const useContentSearch = (searchTerm, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => ContentService.searchContent(searchTerm, institution),
    [searchTerm, institution],
    { ...options, enabled: !!searchTerm && searchTerm.length >= 2 }
  );
};

// Progress Tracking Actions Hook
export const useProgressActions = (userId) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const saveExerciseAttempt = useCallback(async (exerciseData) => {
    if (!userId) {
      setError(new Error('User ID is required'));
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      await ProgressService.saveExerciseAttempt(userId, exerciseData);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId]);

  const updateLessonMastery = useCallback(async (lessonId, masteryData) => {
    if (!userId) {
      setError(new Error('User ID is required'));
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      await ProgressService.updateLessonMastery(userId, lessonId, masteryData);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId]);

  return {
    saveExerciseAttempt,
    updateLessonMastery,
    saving,
    error
  };
};

// NEW: Enhanced Progress Actions Hook with Session Support
export const useSessionProgressActions = (userId, level, chapter, sessionType) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const saveExerciseAttemptWithSession = useCallback(async (exerciseData) => {
    if (!userId) {
      setError(new Error('User ID is required'));
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      console.group(`🔄 useSessionProgressActions: Saving exercise attempt with session`);
      console.log('User ID:', userId);
      console.log('Level:', level, 'Chapter:', chapter, 'Session:', sessionType);
      console.log('Exercise data:', exerciseData);
      console.log('Data path: users/{userId}/level_{level}/chapter_{chapter}/{sessionType}/exercise_attempts/{exerciseId}');
      console.log('Progress path: users/{userId}/level_{level}/chapter_{chapter}/{sessionType}/LearningProgress');
      console.groupEnd();

      await ProgressService.saveExerciseAttemptWithSession(userId, level, chapter, sessionType, exerciseData);
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId, level, chapter, sessionType]);

  const createSessionProgress = useCallback(async () => {
    if (!userId) {
      setError(new Error('User ID is required'));
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      console.group(`🔄 useSessionProgressActions: Creating session progress`);
      console.log('User ID:', userId);
      console.log('Level:', level, 'Chapter:', chapter, 'Session:', sessionType);
      console.log('Progress path: users/{userId}/level_{level}/chapter_{chapter}/{sessionType}/LearningProgress');
      console.groupEnd();

      const progress = await ProgressService.createSessionProgress(userId, level, chapter, sessionType);
      return progress;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId, level, chapter, sessionType]);

  const getLearningProgress = useCallback(async () => {
    if (!userId) {
      setError(new Error('User ID is required'));
      return null;
    }

    try {
      const progress = await ProgressService.getLearningProgress(userId, level, chapter, sessionType);
      return progress;
    } catch (err) {
      setError(err);
      return null;
    }
  }, [userId, level, chapter, sessionType]);

  return {
    saveExerciseAttemptWithSession,
    createSessionProgress,
    getLearningProgress,
    saving,
    error
  };
};

// NEW: Hook for Learning Modules Order - Safe implementation with fallback
export const useLearningModulesOrder = (level, chapter) => {
  const [modulesOrder, setModulesOrder] = useState(fallbackModulesOrder);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let didCancel = false;

    const loadModules = async (retryCount = 1) => {
      try {
        setLoading(true);
        setError(null);

        console.group(`🔧 useLearningModulesOrder: Loading modules order with comprehensive debugging`);
        console.log(`📊 Level: ${level}, Chapter: ${chapter}`);
        console.log(`🔥 Firebase path: feature_testing/Korea_University/LearningModulesOrder/order`);
        console.log(`🔄 Retry attempt: ${retryCount}`);

        const data = await ProgressService.getLearningModulesOrder(level, chapter);

        console.log(`📥 Raw Firebase response:`, data);
        console.log(`📋 Is array:`, Array.isArray(data));
        console.log(`📏 Length:`, data?.length);
        console.log(`📦 Data source:`, data.source || 'unknown');

        if (!didCancel && Array.isArray(data) && data.length > 0) {
          setModulesOrder(data);
          console.log(`✅ Successfully loaded modules order:`, data);
        } else if (!didCancel && data && Array.isArray(data)) {
          // Data is valid array, use it even without source property
          setModulesOrder(data);
          console.log(`✅ Successfully loaded modules order (no source property):`, data);
        } else {
          console.warn('⚠️ Invalid modules order, using fallback');
          console.log(`📋 Fallback modules order:`, fallbackModulesOrder);
          setModulesOrder(fallbackModulesOrder);
        }
      } catch (err) {
        console.error('❌ Module order fetch failed:', err);
        console.error('❌ Error details:', {
          message: err.message,
          code: err.code,
          level,
          chapter,
          retryCount
        });

        if (retryCount > 0) {
          console.log(`🔄 Retrying... (${retryCount} attempts left)`);
          setTimeout(() => loadModules(retryCount - 1), 1000);
        } else {
          setError(err);
          setModulesOrder(fallbackModulesOrder);
          console.log(`🛡️ Using fallback due to error:`, fallbackModulesOrder);
        }
      } finally {
        if (!didCancel) {
          setLoading(false);
          console.groupEnd();
        }
      }
    };

    loadModules();
    return () => {
      didCancel = true;
    };
  }, [level, chapter]);

  return { data: modulesOrder, loading, error };
};

// 🔧 NEW: Critical hook that returns both modulesOrder and moduleMap
export const useLearningModulesConfig = (level, chapter) => {
  const fetchConfig = useCallback(
    () => getLearningModulesConfig(level, chapter),
    [level, chapter]
  );

  return useAsyncData(
    fetchConfig,
    [level, chapter],
    {
      enabled: !!level && !!chapter,
      refetchOnMount: true,
      retryOnError: true,
      maxRetries: 2
    }
  );
};

// NEW: Hook for Uploading Learning Modules Order
export const useUploadLearningModulesOrder = () => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const uploadModulesOrder = useCallback(async (modulesOrderData) => {
    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      console.group(`🔄 useUploadLearningModulesOrder: Uploading modules order`);
      console.log('Modules order data:', modulesOrderData);
      console.log('Target path: feature_testing/Korea_University/LearningModulesOrder');
      console.groupEnd();

      const result = await ProgressService.uploadLearningModulesOrder(modulesOrderData);
      setSuccess(true);
      return result;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setUploading(false);
    }
  }, []);

  return {
    uploadModulesOrder,
    uploading,
    error,
    success
  };
};

// Cache Management Hook
export const useCacheManagement = () => {
  const [cacheStats, setCacheStats] = useState(null);

  const refreshCacheStats = useCallback(() => {
    setCacheStats(DataUtils.getCacheStats());
  }, []);

  const clearAllCache = useCallback(() => {
    DataUtils.clearAllCache();
    refreshCacheStats();
  }, [refreshCacheStats]);

  const clearCacheForType = useCallback((type) => {
    DataUtils.clearCacheForType(type);
    refreshCacheStats();
  }, [refreshCacheStats]);

  useEffect(() => {
    refreshCacheStats();
  }, [refreshCacheStats]);

  return {
    cacheStats,
    refreshCacheStats,
    clearAllCache,
    clearCacheForType
  };
};

// Combined Hook for Level Page
export const useLevelData = (level, institution = 'Korea_University') => {
  const grammarChapters = useGrammarChapters(level, institution);
  const vocabularyChapters = useVocabularyChapters(level, institution);

  const isLoading = grammarChapters.loading || vocabularyChapters.loading;
  const error = grammarChapters.error || vocabularyChapters.error;

  return useMemo(() => ({
    data: {
      level,
      grammarChapters: grammarChapters.data || [],
      vocabularyChapters: vocabularyChapters.data || [],
      hasGrammar: (grammarChapters.data || []).length > 0,
      hasVocabulary: (vocabularyChapters.data || []).length > 0,
      totalChapters: Math.max(
        (grammarChapters.data || []).length,
        (vocabularyChapters.data || []).length
      )
    },
    loading: isLoading,
    error,
    refresh: () => {
      grammarChapters.refresh();
      vocabularyChapters.refresh();
    }
  }), [level, grammarChapters, vocabularyChapters, isLoading, error]);
};

// Combined Hook for Chapter Data
export const useChapterData = (level, chapter, institution = 'Korea_University') => {
  const grammarPoints = useGrammarPoints(level, chapter, institution);
  const vocabulary = useVocabulary(level, chapter, institution);
  const clozeExercises = useClozeExercises(level, chapter, institution);
  const shapePuzzleExercises = useShapePuzzleExercises(level, chapter, institution);
  const translationExercises = useTranslationExercises(level, chapter, institution);

  const isLoading = grammarPoints.loading || vocabulary.loading ||
    clozeExercises.loading || shapePuzzleExercises.loading ||
    translationExercises.loading;

  const error = grammarPoints.error || vocabulary.error ||
    clozeExercises.error || shapePuzzleExercises.error ||
    translationExercises.error;

  return useMemo(() => ({
    data: {
      level,
      chapter,
      grammar: {
        learning: grammarPoints.data?.learning || [],
        exercises: grammarPoints.data?.exercises || [],
        total: grammarPoints.data?.total || 0
      },
      vocabulary: vocabulary.data || [],
      exercises: {
        cloze: clozeExercises.data || [],
        shapePuzzle: shapePuzzleExercises.data || [],
        translation: translationExercises.data || []
      },
      hasContent: (grammarPoints.data?.total || 0) > 0 || (vocabulary.data || []).length > 0
    },
    loading: isLoading,
    error,
    refresh: () => {
      grammarPoints.refresh();
      vocabulary.refresh();
      clozeExercises.refresh();
      shapePuzzleExercises.refresh();
      translationExercises.refresh();
    }
  }), [
    level, chapter, isLoading, error,
    grammarPoints, vocabulary, clozeExercises,
    shapePuzzleExercises, translationExercises
  ]);
};

// Error Boundary Hook
export const useDataErrorBoundary = () => {
  const [error, setError] = useState(null);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((error) => {
    console.error('Data service error:', error);
    setError(error);
  }, []);

  return {
    error,
    resetError,
    handleError
  };
};

// ============================================================================
// TRACKING V2 HOOKS
// ============================================================================

// Main tracking hooks
export const useChapterTrackingV2 = (userId, level, chapter, options = {}) => {
  return useChapterTracking(userId, level, chapter, options);
};

export const useModuleTrackingV2 = (userId, level, chapter, moduleKey, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.learningProgress?.modules?.[moduleKey] || null,
    loading,
    error,
    refresh
  };
};

export const useQuestionTrackingV2 = (userId, level, chapter, moduleKey, questionNum, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.learningProgress?.modules?.[moduleKey]?.questions?.[questionNum] || null,
    loading,
    error,
    refresh
  };
};

// Exposure and mastery hooks
export const useVocabularyMasteryV2 = (userId, level, chapter, wordId, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.masteryData?.vocabulary?.[wordId] || null,
    loading,
    error,
    refresh
  };
};

export const useGrammarMasteryV2 = (userId, level, chapter, grammarId, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.masteryData?.grammar?.[grammarId] || null,
    loading,
    error,
    refresh
  };
};

export const useContentExposureV2 = (userId, level, chapter, contentType, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.masteryData?.[contentType] || null,
    loading,
    error,
    refresh
  };
};

// Data parameters hooks
export const useChapterDataParametersV2 = (userId, level, chapter, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.learningProgress?.chapterDataParameters || null,
    loading,
    error,
    refresh,
    updateParameters: async (parameters) => {
      return DataParametersServiceV2.updateChapterParameters(userId, level, chapter, parameters);
    }
  };
};

export const useModuleDataParametersV2 = (userId, level, chapter, moduleKey, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.learningProgress?.modules?.[moduleKey]?.moduleDataParameters || null,
    loading,
    error,
    refresh,
    updateParameters: async (parameters) => {
      return DataParametersServiceV2.updateModuleParameters(userId, level, chapter, moduleKey, parameters);
    }
  };
};

export const useQuestionDataParametersV2 = (userId, level, chapter, moduleKey, questionNum, options = {}) => {
  const { data, loading, error, refresh } = useChapterTracking(userId, level, chapter, options);

  return {
    data: data?.learningProgress?.modules?.[moduleKey]?.questions?.[questionNum]?.questionDataParameters || null,
    loading,
    error,
    refresh,
    updateParameters: async (parameters) => {
      return DataParametersServiceV2.updateQuestionParameters(userId, level, chapter, moduleKey, questionNum, parameters);
    }
  };
};

// Action hooks
export const useChapterTrackingActionsV2 = (userId, level, chapter) => {
  return {
    initializeDocuments: async (dataParameters = {}) => {
      return TrackingDataServiceV2.initializeChapterTracking(userId, level, chapter, dataParameters);
    },
    markChapterStarted: async (dataParameters = {}) => {
      return TrackingV2Service.markChapterStarted(userId, level, chapter, dataParameters);
    },
    markChapterCompleted: async (dataParameters = {}) => {
      return TrackingV2Service.markChapterCompleted(userId, level, chapter, dataParameters);
    },
    unlockChapter: async (dataParameters = {}) => {
      return TrackingV2Service.unlockChapter(userId, level, chapter, dataParameters);
    },
    updateParameters: async (parameters) => {
      return DataParametersServiceV2.updateChapterParameters(userId, level, chapter, parameters);
    }
  };
};

export const useModuleTrackingActionsV2 = (userId, level, chapter, moduleKey) => {
  return {
    markModuleStarted: async (dataParameters = {}) => {
      return TrackingV2Service.updateModuleProgress(userId, level, chapter, moduleKey, {
        userStarted: true,
        startDate: new Date().toISOString(),
        lastOpened: new Date().toISOString()
      }, dataParameters);
    },
    markModuleCompleted: async (dataParameters = {}) => {
      return TrackingV2Service.updateModuleProgress(userId, level, chapter, moduleKey, {
        completion: true,
        completionTime: new Date().toISOString()
      }, dataParameters);
    },
    updateProgress: async (progressData, dataParameters = {}) => {
      return TrackingV2Service.updateModuleProgress(userId, level, chapter, moduleKey, progressData, dataParameters);
    },
    updateParameters: async (parameters) => {
      return DataParametersServiceV2.updateModuleParameters(userId, level, chapter, moduleKey, parameters);
    }
  };
};

export const useQuestionTrackingActionsV2 = (userId, level, chapter, moduleKey, questionNum) => {
  return {
    addQuestionAttempt: async (questionData, dataParameters = {}) => {
      return TrackingV2Service.addQuestionAttempt(userId, level, chapter, moduleKey, questionNum, questionData, dataParameters);
    },
    updateProgress: async (progressData, dataParameters = {}) => {
      return TrackingV2Service.addQuestionAttempt(userId, level, chapter, moduleKey, questionNum, progressData, dataParameters);
    },
    updateParameters: async (parameters) => {
      return DataParametersServiceV2.updateQuestionParameters(userId, level, chapter, moduleKey, questionNum, parameters);
    }
  };
};

export const useExposureTrackingActionsV2 = (userId, level, chapter) => {
  return {
    markVocabularyExposed: async (wordId, moduleKey, dataParameters = {}) => {
      return TrackingV2Service.markVocabularyExposed(userId, level, chapter, wordId, moduleKey, dataParameters);
    },
    markGrammarExposed: async (grammarId, moduleKey, dataParameters = {}) => {
      return TrackingV2Service.markGrammarExposed(userId, level, chapter, grammarId, moduleKey, dataParameters);
    },
    markDialogueExposed: async (dialogueId, moduleKey, dataParameters = {}) => {
      return TrackingV2Service.markDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
    },
    markIntroDialogueExposed: async (dialogueId, moduleKey, dataParameters = {}) => {
      return TrackingV2Service.markIntroDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
    },
    markConclusionDialogueExposed: async (dialogueId, moduleKey, dataParameters = {}) => {
      return TrackingV2Service.markConclusionDialogueExposed(userId, level, chapter, dialogueId, moduleKey, dataParameters);
    },
    markComprehensionExposed: async (comprehensionId, moduleKey, dataParameters = {}) => {
      return TrackingV2Service.markComprehensionExposed(userId, level, chapter, comprehensionId, moduleKey, dataParameters);
    },
    markAllVocabularyExposed: async (wordIds, moduleKey, dataParameters = {}) => {
      const promises = wordIds.map(wordId =>
        TrackingV2Service.markVocabularyExposed(userId, level, chapter, wordId, moduleKey, dataParameters)
      );
      return Promise.all(promises);
    },
    markAllGrammarExposed: async (grammarIds, moduleKey, dataParameters = {}) => {
      const promises = grammarIds.map(grammarId =>
        TrackingV2Service.markGrammarExposed(userId, level, chapter, grammarId, moduleKey, dataParameters)
      );
      return Promise.all(promises);
    }
  };
};

export const useMasteryTrackingActionsV2 = (userId, level, chapter) => {
  return {
    updateVocabularyMastery: async (wordId, moduleKey, masteryData, dataParameters = {}) => {
      return TrackingV2Service.updateVocabularyMastery(userId, level, chapter, wordId, moduleKey, masteryData, dataParameters);
    },
    updateGrammarMastery: async (grammarId, moduleKey, masteryData, dataParameters = {}) => {
      return TrackingV2Service.updateGrammarMastery(userId, level, chapter, grammarId, moduleKey, masteryData, dataParameters);
    },
    updateDialogueMastery: async (dialogueId, moduleKey, masteryData, dataParameters = {}) => {
      return TrackingV2Service.updateDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
    },
    updateIntroDialogueMastery: async (dialogueId, moduleKey, masteryData, dataParameters = {}) => {
      return TrackingV2Service.updateIntroDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
    },
    updateConclusionDialogueMastery: async (dialogueId, moduleKey, masteryData, dataParameters = {}) => {
      return TrackingV2Service.updateConclusionDialogueMastery(userId, level, chapter, dialogueId, moduleKey, masteryData, dataParameters);
    },
    updateComprehensionMastery: async (comprehensionId, moduleKey, masteryData, dataParameters = {}) => {
      return TrackingV2Service.updateComprehensionMastery(userId, level, chapter, comprehensionId, moduleKey, masteryData, dataParameters);
    }
  };
};

// Content discovery hooks
export const useContentDiscoveryV2 = (level, chapter, institution = 'Korea_University', options = {}) => {
  return useContentDiscovery(level, chapter, institution, options);
};

// Legacy compatibility hooks
export const useLearningProgressV2 = (userId, level, chapter, options = {}) => {
  return useLearningProgress(userId, level, chapter, options);
};

export const useMasteryDataV2 = (userId, level, chapter, options = {}) => {
  return useMasteryData(userId, level, chapter, options);
};

export const useKnowledgeProfileV2 = (userId, level, chapter, sessionType = 'Learn', options = {}) => {
  return useKnowledgeProfile(userId, level, chapter, sessionType, options);
};

/**
 * Hook to get comprehension data
 */
export const useComprehensionData = (level, chapter, comprehensionId, institution = 'Korea_University', options = {}) => {
  return useAsyncData(
    () => DataService.getComprehensionData(level, chapter, comprehensionId, institution),
    [level, chapter, comprehensionId, institution],
    options
  );
};

/**
 * Hook to discover dialogue content for a chapter
 * TODO: Implement ContentDiscoveryServiceV2
 */
// export const useDialogueContentDiscovery = (level, chapter, institution = 'Korea_University', options = {}) => {
//   return useAsyncData(
//     () => ContentDiscoveryServiceV2.discoverDialogueContent(level, chapter, institution),
//     [level, chapter, institution],
//     options
//   );
// };

/**
 * Hook to get specific dialogue content by type
 * TODO: Implement ContentDiscoveryServiceV2
 */
// export const useDialogueContent = (level, chapter, dialogueType, institution = 'Korea_University', options = {}) => {
//   return useAsyncData(
//     () => ContentDiscoveryServiceV2.getDialogueContent(level, chapter, dialogueType, institution),
//     [level, chapter, dialogueType, institution],
//     { ...options, enabled: !!dialogueType }
//   );
// };

/**
 * Hook to get comprehension mastery data
 */
export const useComprehensionMasteryV2 = (userId, level, chapter, comprehensionId, options = {}) => {
  return useAsyncData(
    () => TrackingV2Service.getMasteryData(userId, level, chapter).then(data =>
      data?.comprehension?.[comprehensionId] || null
    ),
    [userId, level, chapter, comprehensionId],
    options
  );
};

// Export tracking hooks
export {
  useKnowledgeProfile,
  useMasteryData,
  useExposureData,
  useChapterProgress
} from './tracking';

// Export useChapterTracking from tracking hooks (same source as useDataService import)
export { useChapterTracking } from '../tracking/hooks.js';

// Export all hooks
const useDataService = {
  // Grammar hooks
  useGrammarPoints,
  useGrammarPoint,
  useGrammarChapters,
  useComprehensiveGrammar,
  useGrammarExercises,

  // Vocabulary hooks
  useVocabulary,
  useVocabularyWord,
  useVocabularyChapters,
  useVocabularySearch,

  // Dialogue hooks
  useDialogueData,
  useDialogueScene,

  // Exercise hooks
  useClozeExercises,
  useShapePuzzleExercises,
  useTranslationExercises,

  // Progress hooks
  useUserProgress,
  useLessonMastery,
  useProgressActions,
  useSessionProgressActions,
  useLearningModulesOrder,
  useLearningModulesConfig,
  useUploadLearningModulesOrder,

  // Content hooks
  useAvailableContent,
  useContentSearch,

  // Utility hooks
  useCacheManagement,
  useLevelData,
  useChapterData,
  useDataErrorBoundary,

  // Tracking hooks (from tracking/hooks.js)
  useChapterTracking,

  // Tracking V2 hooks
  useChapterTrackingV2,
  useModuleTrackingV2,
  useQuestionTrackingV2,
  useVocabularyMasteryV2,
  useGrammarMasteryV2,
  useContentExposureV2,
  useChapterDataParametersV2,
  useModuleDataParametersV2,
  useQuestionDataParametersV2,
  useChapterTrackingActionsV2,
  useModuleTrackingActionsV2,
  useQuestionTrackingActionsV2,
  useExposureTrackingActionsV2,
  useMasteryTrackingActionsV2,
  useContentDiscoveryV2,
  useLearningProgressV2,
  useMasteryDataV2,
  useKnowledgeProfileV2
};

export default useDataService;
